import { apiClient } from '../../lib/api-client';
import type {
  BillingDetailsFilterOptions,
  BillingDetailsFilters,
  BirthReportFilters,
  BirthReportRow,
  BirthReportSummary,
  CollectionDetailFilters,
  CollectionDetailResponse,
  CollectionReportResponse,
  CommissionReportFilters,
  CommissionLineInput,
  CommissionRecord,
  CommissionReportRow,
  CommissionReportSummary,
  DailyReportResponse,
  DischargeReportFilters,
  DischargeReportRow,
  DischargeReportSummary,
  DoctorPayoutRow,
  DoctorPayoutSummary,
  HospitalStayFilters,
  HospitalStayReportResponse,
  InvestigationReportFilters,
  InvestigationReportRow,
  InvestigationReportSummary,
  PaginatedReport,
  PatientReportFilters,
  PatientReportModule,
  PatientReportRow,
  PatientReportSummary,
  ReportBillRow,
  BillReportSummary,
  BillingDetailRow,
  BillingDetailSummary,
  ReportListFilters,
  ReportReceiptRow,
  ReceiptReportSummary,
  ReportRefundRow,
  RefundReportSummary,
  RevenueReportRow,
  ReportSection,
} from './types';

interface ApiResponse<T> { success: true; data: T }

const compactParams = (params: object) => Object.fromEntries(
  Object.entries(params).filter(([, value]) => value !== '' && value !== undefined && value !== null),
);

export const fetchCollectionReport = async (filters: { fromDate?: string; toDate?: string }) => {
  const response = await apiClient.get<ApiResponse<CollectionReportResponse>>('/reports/collection', { params: compactParams(filters) });
  return response.data.data;
};

export const fetchDailyReport = async (date: string) => {
  const response = await apiClient.get<ApiResponse<DailyReportResponse>>('/reports/daily', { params: { date } });
  return response.data.data;
};

const fetchAllReportPages = async <T, S>(path: string, params: Record<string, string | number | null | undefined>): Promise<T[]> => {
  const firstResponse = await apiClient.get<PaginatedReport<T, S>>(path, { params: compactParams({ ...params, page: 1, limit: 500 }) });
  const firstPage = firstResponse.data;
  if (firstPage.pagination.totalPages <= 1) return firstPage.data;
  const remainingPages = await Promise.all(Array.from(
    { length: firstPage.pagination.totalPages - 1 },
    (_, index) => apiClient.get<PaginatedReport<T, S>>(path, { params: compactParams({ ...params, page: index + 2, limit: 500 }) }),
  ));
  return [firstPage.data, ...remainingPages.map((response) => response.data.data)].flat();
};

export const fetchCollectionDetails = async (filters: CollectionDetailFilters): Promise<CollectionDetailResponse> => {
  const params = {
    fromDate: filters.fromDate,
    toDate: filters.toDate,
    section: filters.section,
    userId: filters.userId,
    userName: filters.userName,
    sortBy: 'date',
    sortOrder: 'asc',
  };
  const [receipts, refunds] = await Promise.all([
    fetchAllReportPages<ReportReceiptRow, ReceiptReportSummary>('/reports/receipts', params),
    filters.section === null
      ? fetchAllReportPages<ReportRefundRow, RefundReportSummary>('/reports/refunds', params)
      : Promise.resolve([]),
  ]);
  const paymentRows = receipts.map((row) => ({
    id: `receipt:${row.id}`,
    sourceId: row.sourceId,
    admissionId: row.admissionId,
    module: row.module,
    billId: row.billId,
    patientId: row.patientId,
    patientName: row.patientName,
    uhid: row.uhid,
    amount: row.amount,
    date: row.date,
    mode: row.mode,
    account: row.bankName,
    isRefund: false,
  }));
  const refundRows = refunds.map((row) => ({
    id: `refund:${row.id}`,
    sourceId: row.sourceId,
    admissionId: row.admissionId,
    module: row.module,
    billId: row.billId,
    patientId: row.patientId,
    patientName: row.patientName,
    uhid: row.uhid,
    amount: -row.amount,
    date: row.date,
    mode: 'Refund',
    account: null,
    isRefund: true,
  }));
  const rows = [...paymentRows, ...refundRows].sort((left, right) => new Date(left.date).getTime() - new Date(right.date).getTime());
  const cashReceipts = receipts.filter((row) => row.mode.trim().toLowerCase() === 'cash').reduce((sum, row) => sum + row.amount, 0);
  const bankReceipts = receipts.filter((row) => row.mode.trim().toLowerCase() !== 'cash').reduce((sum, row) => sum + row.amount, 0);
  const refundTotal = refunds.reduce((sum, row) => sum + row.amount, 0);
  return {
    rows,
    totals: {
      cash: cashReceipts - refundTotal,
      bank: bankReceipts,
      refund: refundTotal,
      tds: 0,
      total: cashReceipts + bankReceipts - refundTotal,
    },
  };
};

export const fetchMyCollectionReport = async (filters: { fromDate?: string; toDate?: string }) => {
  const response = await apiClient.get<ApiResponse<CollectionReportResponse>>('/reports/my-collection', { params: compactParams(filters) });
  return response.data.data;
};

export const fetchRevenueReport = async (filters: { fromDate?: string; toDate?: string }) => {
  const response = await apiClient.get<ApiResponse<RevenueReportRow[]>>('/reports/revenue', { params: compactParams(filters) });
  return response.data.data;
};

export const fetchHospitalStayReport = async (filters: HospitalStayFilters) => {
  const response = await apiClient.get<ApiResponse<HospitalStayReportResponse>>('/reports/hospital-stay', { params: compactParams(filters) });
  return response.data.data;
};

export const fetchReportBills = async (filters: ReportListFilters) => {
  const response = await apiClient.get<PaginatedReport<ReportBillRow, BillReportSummary>>('/reports/bills', { params: compactParams(filters) });
  return response.data;
};

export const fetchReportReceipts = async (filters: ReportListFilters) => {
  const response = await apiClient.get<PaginatedReport<ReportReceiptRow, ReceiptReportSummary>>('/reports/receipts', { params: compactParams(filters) });
  return response.data;
};

export const fetchReportRefunds = async (filters: ReportListFilters) => {
  const response = await apiClient.get<PaginatedReport<ReportRefundRow, RefundReportSummary>>('/reports/refunds', { params: compactParams(filters) });
  return response.data;
};

export const fetchReportBillingDetails = async (filters: BillingDetailsFilters) => {
  const response = await apiClient.get<PaginatedReport<BillingDetailRow, BillingDetailSummary>>('/reports/billing-details', { params: compactParams(filters) });
  return response.data;
};

export const fetchBillingDetailsFilterOptions = async () => {
  const response = await apiClient.get<ApiResponse<BillingDetailsFilterOptions>>('/reports/billing-details/filter-options');
  return response.data.data;
};

export const fetchPatientReport = async (module: PatientReportModule, filters: PatientReportFilters) => {
  const response = await apiClient.get<PaginatedReport<PatientReportRow, PatientReportSummary>>(`/reports/patients/${module}`, { params: compactParams(filters) });
  return response.data;
};

export const fetchInvestigationReport = async (filters: InvestigationReportFilters) => {
  const response = await apiClient.get<PaginatedReport<InvestigationReportRow, InvestigationReportSummary>>('/reports/investigation', { params: compactParams(filters) });
  return response.data;
};

export const fetchDoctorPayoutReport = async (filters: ReportListFilters) => {
  const response = await apiClient.get<PaginatedReport<DoctorPayoutRow, DoctorPayoutSummary>>('/reports/doctor-payout', { params: compactParams(filters) });
  return response.data;
};

export const fetchCommissionReport = async (filters: CommissionReportFilters) => {
  const response = await apiClient.get<PaginatedReport<CommissionReportRow, CommissionReportSummary>>('/reports/commission', { params: compactParams(filters) });
  return response.data;
};

export const createCommission = async (payload: { module: ReportSection; sourceId: string; lines: CommissionLineInput[] }) => {
  const response = await apiClient.post<ApiResponse<CommissionRecord>>('/reports/commission', payload);
  return response.data.data;
};

export const updateCommission = async (id: string, lines: CommissionLineInput[]) => {
  const response = await apiClient.put<ApiResponse<CommissionRecord>>(`/reports/commission/${id}`, { lines });
  return response.data.data;
};

export const deleteCommission = async (id: string) => {
  await apiClient.delete(`/reports/commission/${id}`);
};

export const approveCommission = async (id: string) => {
  const response = await apiClient.post<ApiResponse<CommissionRecord>>(`/reports/commission/${id}/approve`);
  return response.data.data;
};

export const payCommission = async (id: string) => {
  const response = await apiClient.post<ApiResponse<CommissionRecord>>(`/reports/commission/${id}/pay`);
  return response.data.data;
};

export const fetchBirthReport = async (filters: BirthReportFilters) => {
  const response = await apiClient.get<PaginatedReport<BirthReportRow, BirthReportSummary>>('/reports/birth', { params: compactParams(filters) });
  return response.data;
};

export const fetchDeathReport = async (filters: ReportListFilters) => {
  const response = await apiClient.get<PaginatedReport<DischargeReportRow, DischargeReportSummary>>('/reports/death', { params: compactParams(filters) });
  return response.data;
};

export const fetchDischargeReport = async (filters: DischargeReportFilters) => {
  const response = await apiClient.get<PaginatedReport<DischargeReportRow, DischargeReportSummary>>('/reports/discharge', { params: compactParams(filters) });
  return response.data;
};

export const reportSections: { value: '' | ReportSection; label: string }[] = [
  { value: '', label: 'All Sections' },
  { value: 'opd', label: 'OPD' },
  { value: 'emg', label: 'EMG' },
  { value: 'ipd', label: 'IPD' },
  { value: 'daycare', label: 'Daycare' },
  { value: 'dialysis', label: 'Dialysis' },
  { value: 'ot', label: 'OT' },
  { value: 'investigation', label: 'Investigation' },
];

